import { useState, useEffect } from 'react';
import type { GpsPosition } from '../sensors/gps';
import type { HeadingData } from '../sensors/heading';
import type { MotionData } from '../sensors/motion';
import { getMotionManager } from '../sensors/motion';

interface HudIndicatorsProps {
  position: GpsPosition | null;
  heading: HeadingData | null;
  motion: MotionData | null;
}

export function HudIndicators({ position, heading, motion }: HudIndicatorsProps) {
  const [showMotionButton, setShowMotionButton] = useState(false);

  const speedKmh = position?.speed !== null && position?.speed !== undefined
    ? Math.round(position.speed * 3.6) 
    : 0;
  
  const accuracyM = position?.accuracy 
    ? Math.round(position.accuracy) 
    : null;
  
  const gpsHz = heading?.gpsHz 
    ? heading.gpsHz.toFixed(1) 
    : '0.0';

  const sourceLabel = heading?.source === 'trajectory' 
    ? 'Trajectoire' 
    : heading?.source === 'waiting' 
    ? 'Calcul...' 
    : '--';

  // Valeurs accéléromètre
  const magnitude = motion?.available ? motion.magnitude.toFixed(1) : 'N/A';
  const accelHz = motion?.available ? motion.accelHz.toFixed(1) : '0.0';


  const handleEnableMotion = async () => {
    const manager = getMotionManager();
    if (manager) {
      const granted = await manager.requestMotionPermission();
      if (granted) {
        setShowMotionButton(false);
      }
    }
  };


  useEffect(() => {
    const checkMotionNeeded = () => {
      if (motion && !motion.available && !showMotionButton) {
        setShowMotionButton(true);
      }
    };
    checkMotionNeeded();
  }, [motion, showMotionButton]);

  return (
    <div className="absolute top-0 left-0 right-0 z-30 pointer-events-none">
      <div className="bg-black/80 backdrop-blur-sm border-b border-white/10">
        <div className="px-3 py-1">
          <div className="flex items-start justify-between">
            <div className="flex-1 grid grid-cols-6 gap-1 text-center text-sm">
              <div className="flex flex-col">
                <span className="text-xs text-white/60">km/h</span>
                <span className="text-lg font-bold text-white">
                  {speedKmh}
                </span>
              </div>
              
              <div className="flex flex-col">
                <span className="text-xs text-white/60 ">Précision</span>
                <span className="text-sm font-medium text-white">
                  {accuracyM !== null ? `${accuracyM}m` : '--'}
                </span>
              </div>
              
              <div className="flex flex-col">
                <span className="text-xs text-white/60 ">GPS Hz</span>
                <span className="text-sm font-medium text-white">
                  {gpsHz}
                </span>
              </div>
              
              <div className="flex flex-col">
                <span className="text-xs text-white/60 ">Cap</span>
                <span className="text-sm font-medium text-white">
                  {sourceLabel}
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-white/60 ">|a| m/s²</span>
                <span className="text-sm font-medium text-white">
                  {magnitude}
                </span>
              </div>
              
              <div className="flex flex-col">
                <span className="text-xs text-white/60 ">Accel Hz</span>
                <span className="text-sm font-medium text-white">
                  {accelHz}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              {showMotionButton && (
                <button
                  onClick={handleEnableMotion}
                  className="pointer-events-auto px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                >
                  Accéléro
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}